import { __esDecorate, __runInitializers } from "tslib";
import { isDefined, isEmpty, isString } from '#library/assertion.library.js';
import { StringTag } from '#library/decorator.library.js';
/**
 * ## BoundedCache
 * High-performance Map implementation enforcing maximum capacity (LRU) and TTL eviction.
 * Supports static keys (immortal glossary definitions) and date-salt staleness purging.
 */
let BoundedCache = (() => {
    let _classDecorators = [StringTag('BoundedCache')];
    let _classDescriptor;
    let _classExtraInitializers = [];
    let _classThis;
    let _classSuper = Map;
    var BoundedCache = class extends _classSuper {
        static { _classThis = this; }
        static {
            const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(_classSuper[Symbol.metadata] ?? null) : void 0;
            __esDecorate(null, _classDescriptor = { value: _classThis }, _classDecorators, { kind: "class", name: _classThis.name, metadata: _metadata }, null, _classExtraInitializers);
            BoundedCache = _classThis = _classDescriptor.value;
            if (_metadata) Object.defineProperty(_classThis, Symbol.metadata, { enumerable: true, configurable: true, writable: true, value: _metadata });
            __runInitializers(_classThis, _classExtraInitializers);
        }
        #expires = new Map();
        #staticKeys = new Set();
        #ttls = new Map();
        maxSize;
        ttl;
        /**
         * Creates a bounded cache with configurable capacity and entry lifetime.
         *
         * @param maxSize - Maximum number of non-static entries retained
         * @param ttl - Default time-to-live for entries in milliseconds
         */
        constructor(maxSize = 1000, ttl = 24 * 60 * 60 * 1000) {
            super();
            this.maxSize = maxSize;
            this.ttl = ttl;
        }
        /**
         * Returns true to identify this instance as a BoundedCache.
         */
        get isBoundedCache() {
            return true;
        }
        /**
         * Determines whether a cache entry has passed its expiration deadline.
         *
         * @param key - Cache key to inspect
         * @returns True when the entry has expired, otherwise false
         */
        #isExpired(key) {
            const deadline = this.#expires.get(key);
            if (deadline === undefined)
                return false;
            return Date.now() > deadline;
        }
        /**
         * Purges TTL-expired entries.
         */
        evictExpired() {
            if (this.#expires.size === 0)
                return;
            const now = Date.now();
            for (const [key, deadline] of this.#expires.entries()) {
                if (now > deadline) {
                    this.delete(key);
                }
            }
        }
        /**
         * Register an immortal static key (e.g. business glossary term).
         * Static keys bypass TTL expiration and LRU capacity eviction.
         */
        setStatic(key, value) {
            if (super.has(key))
                super.delete(key);
            super.set(key, value);
            this.#staticKeys.add(key);
            this.#expires.delete(key);
            this.#ttls.delete(key);
            return this;
        }
        /**
         * Check if a key is a static key.
         */
        isStatic(key) {
            return this.#staticKeys.has(key);
        }
        /**
         * Retrieves a value from the cache by key. Evaluates TTL expiration via `#expires`
         * specifically for the requested key, and updates LRU ordering by deleting and re-inserting
         * the key in the underlying Map. Leaves the expiration deadline unchanged.
         *
         * @param key - The cache key to retrieve
         * @returns The cached value, or undefined if not found or expired
         */
        get(key) {
            if (this.#isExpired(key)) {
                this.delete(key);
                return undefined;
            }
            if (!super.has(key))
                return undefined;
            const val = super.get(key);
            super.delete(key);
            super.set(key, val);
            return val;
        }
        /**
         * Checks if a key exists in the cache and is not expired.
         * Automatically evicts expired entries on access.
         *
         * @param key - The cache key to check
         * @returns True if the key exists and is not expired, false otherwise
         */
        has(key) {
            if (this.#isExpired(key)) {
                this.delete(key);
                return false;
            }
            return super.has(key);
        }
        /**
         * Returns the number of entries in the cache after evicting expired entries.
         */
        get size() {
            this.evictExpired();
            return super.size;
        }
        /**
         * Executes a callback for each cache entry after evicting expired entries.
         *
         * @param callbackfn - Function to execute for each entry
         * @param thisArg - Optional value to use as `this` when executing the callback
         */
        forEach(callbackfn, thisArg) {
            this.evictExpired();
            super.forEach(callbackfn, thisArg);
        }
        /**
         * Sets a key-value pair in the cache, enforcing LRU eviction.
         * If finite-TTL is configured, entries retain their original absolute expiration deadline
         * unless an explicit `ttl` override is passed.
         * If the cache exceeds maxSize, the oldest non-static entry is removed.
         *
         * @param key - The cache key to set
         * @param value - The value to store
         * @param ttl - Optional custom time-to-live in milliseconds for this specific entry
         * @returns This cache instance for chaining
         */
        set(key, value, ttl) {
            this.evictExpired();
            const existingDeadline = this.#expires.get(key);
            if (super.has(key))
                super.delete(key);
            super.set(key, value);
            this.#staticKeys.delete(key);
            if (isDefined(ttl)) {
                if (ttl !== Infinity) {
                    this.#expires.set(key, Date.now() + ttl);
                    this.#ttls.set(key, ttl);
                }
                else {
                    this.#expires.delete(key);
                    this.#ttls.delete(key);
                }
            }
            else if (existingDeadline !== undefined) {
                // Retain existing absolute expiration deadline during cache updates
                this.#expires.set(key, existingDeadline);
            }
            else if (this.ttl !== Infinity) {
                this.#expires.set(key, Date.now() + this.ttl);
                this.#ttls.delete(key);
            }
            else {
                this.#expires.delete(key);
                this.#ttls.delete(key);
            }
            while ((this.size - this.#staticKeys.size) > this.maxSize) {
                let evicted = false;
                const keysIter = super.keys();
                let res = keysIter.next();
                while (!res.done) {
                    const k = res.value;
                    if (!this.#staticKeys.has(k)) {
                        this.delete(k);
                        evicted = true;
                        break;
                    }
                    res = keysIter.next();
                }
                if (!evicted)
                    break;
            }
            return this;
        }
        /**
         * Deletes a key from the cache, removing its expiration and static key markers.
         *
         * @param key - The cache key to delete
         * @returns True if the key was deleted, false if it did not exist
         */
        delete(key) {
            this.#staticKeys.delete(key);
            this.#expires.delete(key);
            this.#ttls.delete(key);
            return super.delete(key);
        }
        /**
         * Deletes all cache entries whose keys start with the specified prefix.
         * The prefix comparison is case-insensitive.
         *
         * @param prefix - The prefix string to match against cache keys
         * @returns The number of entries deleted
         */
        deletePrefix(prefix) {
            const normalizedPrefix = String(prefix).trim().toLowerCase();
            if (isEmpty(normalizedPrefix))
                return 0;
            const toDelete = [];
            for (const key of super.keys()) {
                if (isString(key) && key.toLowerCase().startsWith(normalizedPrefix))
                    toDelete.push(key);
            }
            for (const k of toDelete)
                this.delete(k);
            return toDelete.length;
        }
        /**
         * Purges cache entries.
         * If `count` is specified, evicts up to `count` oldest non-static entries.
         * If omitted, clears all entries, including static entries.
         *
         * @param count - Optional number of entries to evict. If omitted, clears all entries.
         */
        clear(count) {
            if (count === undefined) {
                this.#staticKeys.clear();
                this.#expires.clear();
                this.#ttls.clear();
                super.clear();
                return;
            }
            let evicted = 0;
            for (const key of super.keys()) {
                if (evicted >= count)
                    break;
                if (!this.#staticKeys.has(key)) {
                    this.delete(key);
                    evicted++;
                }
            }
        }
        /**
         * Returns an iterator of cache keys after evicting expired entries.
         *
         * @returns Iterator of cache keys
         */
        keys() {
            this.evictExpired();
            return super.keys();
        }
        /**
         * Returns an iterator of cache values after evicting expired entries.
         *
         * @returns Iterator of cache values
         */
        values() {
            this.evictExpired();
            return super.values();
        }
        /**
         * Returns an iterator of cache entries after evicting expired entries.
         *
         * @returns Iterator of [key, value] pairs
         */
        entries() {
            this.evictExpired();
            return super.entries();
        }
        /**
         * Returns an iterator of cache entries after evicting expired entries, making the cache iterable.
         *
         * @returns Iterator of [key, value] pairs
         */
        [Symbol.iterator]() {
            this.evictExpired();
            return super[Symbol.iterator]();
        }
        /**
         * Returns a plain key-value object of all active non-expired cache entries.
         * Filters out non-string keys to prevent lossy key conversions or collisions (e.g. numeric 1 vs string "1").
         */
        toJSON() {
            this.evictExpired();
            const stringEntries = [];
            for (const [k, v] of super.entries()) {
                if (isString(k))
                    stringEntries.push([k, v]);
            }
            return Object.fromEntries(stringEntries);
        }
        /**
         * Creates a BoundedCache from an iterable of key-value pairs.
         *
         * @param entries - Iterable of [key, value] pairs to populate the cache
         * @param maxSize - Maximum number of entries (default: 1000)
         * @param ttl - Time-to-live in milliseconds (default: 24 hours)
         * @returns A new BoundedCache instance
         */
        static fromEntries(entries, maxSize = 1000, ttl = 24 * 60 * 60 * 1000) {
            const cache = new BoundedCache(maxSize, ttl);
            for (const [k, v] of entries) {
                cache.set(k, v);
            }
            return cache;
        }
    };
    return BoundedCache = _classThis;
})();
export { BoundedCache };
